import React from 'react';
import LightModeIcon from '@mui/icons-material/LightMode';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import { Box, Button, CardActions, Typography } from '@mui/material';
import {
  EditButton,
  FunctionField,
  ReferenceField,
  TextField,
  useAuthProvider,
  useNotify,
  useRecordContext,
} from 'react-admin';
import { OnlineField } from '../../components/device';
import environment from '../../lib/reactAppEnv';
import { ConfirmationDialog, type ConfirmationDialogProps } from '../../ui/ConfirmationDialog';
import DeviceOverscan from '../../ui/DeviceOverscan';
import DevicePublicUrl from '../../ui/DevicePublicUrl';
import DeviceSplashLogo from '../../ui/DeviceSplashLogo';
import DeviceVncPreview from '../../ui/DeviceVncPreview';
import type { RaRecord } from 'react-admin';

const styles = {
  actionCard: {
    'padding': 0,
    'flexWrap': 'wrap',
    '& .MuiButton-root': {
      'marginTop': '2em',
      'marginRight': '1em',

      '.MuiButton-icon': {
        marginRight: '6px !important',
      },
    },
  },
};

type DeviceRecord = RaRecord & {
  'uuid': string;
  'device name': string;
  'api heartbeat state'?: string;
};

const ControlsWidget: React.FC = () => {
  const authProvider = useAuthProvider();
  const notify = useNotify();
  const record = useRecordContext<DeviceRecord>();

  const [confirmationDialog, setConfirmationDialog] = React.useState<ConfirmationDialogProps | null>(null);

  const invokeSupervisor = React.useCallback(
    async (device: DeviceRecord, command: string) => {
      const session = authProvider?.getSession?.();
      const remoteBase = environment.REACT_APP_OPEN_BALENA_REMOTE_URL ?? '';
      try {
        const resp = await fetch(`${remoteBase}/supervisor/${command}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uuid: device.uuid, jwt: session?.jwt }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({ error: resp.statusText })) as { error?: string };
          throw new Error(err.error ?? resp.statusText);
        }
        notify(`Successfully executed command ${command} on device ${device['device name']}`, { type: 'success' });
      } catch (error) {
        notify(`Error: Could not execute command ${command} on device ${device['device name']}`, { type: 'error' });
      }
    },
    [authProvider, notify],
  );

  const sendWol = React.useCallback(async (device: DeviceRecord) => {
    const session = authProvider?.getSession?.();
    const remoteBase = environment.REACT_APP_OPEN_BALENA_REMOTE_URL ?? '';
    try {
      const resp = await fetch(`${remoteBase}/wol/${device.uuid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jwt: session?.jwt }),
      });
      const data = await resp.json() as { ok?: boolean; mac?: string; error?: string };
      if (!resp.ok) throw new Error(data.error ?? resp.statusText);
      notify(`Wake-on-LAN verstuurd naar ${data.mac}`, { type: 'success' });
    } catch (e) {
      notify(`WoL mislukt: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' });
    }
  }, [authProvider, notify]);

  if (!record) return null;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Left: device info and controls */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='h5' component='h2' gutterBottom>
            {record['device name']}
          </Typography>

          <Box maxWidth='40em'>
            <p style={{ marginBottom: '5px' }}>
              <b>Fleet: </b>
              <ReferenceField source='belongs to-application' reference='application' target='id'>
                <TextField source='app name' style={{ fontSize: '12pt' }} />
              </ReferenceField>
            </p>

            <p style={{ margin: 0 }}>
              <b>Status: </b>
              <OnlineField source='api heartbeat state' />
            </p>
          </Box>

          <CardActions sx={styles.actionCard}>
            <FunctionField
              render={(fieldRecord: DeviceRecord) => {
                const isOffline = fieldRecord['api heartbeat state'] !== 'online';

                return (
                  <>
                    <EditButton label='Edit' size='medium' variant='outlined' color='secondary' />

                    <Button
                      variant='outlined'
                      size='medium'
                      onClick={() => invokeSupervisor(fieldRecord, 'blink')}
                      startIcon={<LightModeIcon />}
                      disabled={isOffline}
                    >
                      Blink
                    </Button>

                    <Button
                      variant='outlined'
                      size='medium'
                      startIcon={<RestartAltIcon />}
                      disabled={isOffline}
                      onClick={() => {
                        setConfirmationDialog({
                          title: 'Reboot Device',
                          content: 'Are you sure you want to reboot this device?',
                          onConfirm: () => invokeSupervisor(fieldRecord, 'reboot'),
                        });
                      }}
                    >
                      Reboot
                    </Button>

                    <Button
                      variant='outlined'
                      size='medium'
                      startIcon={<PowerSettingsNewIcon />}
                      disabled={isOffline}
                      onClick={() => {
                        setConfirmationDialog({
                          title: 'Shutdown Device',
                          content: 'Are you sure you want to shut down this device?',
                          onConfirm: () => invokeSupervisor(fieldRecord, 'shutdown'),
                        });
                      }}
                    >
                      Shutdown
                    </Button>

                    <Button
                      variant='outlined'
                      size='medium'
                      startIcon={<WifiIcon />}
                      onClick={() => sendWol(fieldRecord)}
                    >
                      Wake
                    </Button>
                  </>
                );
              }}
            />
          </CardActions>

          <DevicePublicUrl />
          <DeviceSplashLogo />
          <DeviceOverscan />
        </Box>

        {/* Right: display preview */}
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <DeviceVncPreview />
        </Box>
      </Box>

      {!!confirmationDialog && (
        <ConfirmationDialog {...confirmationDialog} onClose={() => setConfirmationDialog(null)} />
      )}
    </>
  );
};

export default ControlsWidget;
